import type { Country } from '../types.js';
import { CountryCombobox } from './CountryCombobox.js';

const europeanUnion = new Set('AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE'.split(' '));

// Websites were confirmed on 23 September 2026 as each provider's own decant or sample shop.
// Providers without a confirmed website stay listed by name only.
type Provider = { name: string; url?: string };

const providersByCountry: Record<string, readonly Provider[]> = {
  GB: [{ name: 'Fragrance Samples UK', url: 'https://www.fragrancesamplesuk.com/' }, { name: 'Visionary Fragrances', url: 'https://visionaryfragrances.com/' }, { name: 'Decanted', url: 'https://decanted.uk/' }, { name: 'Scent Samples UK', url: 'https://scentsamples.uk.com/' }],
  US: [{ name: 'Scent Split', url: 'https://www.scentsplit.com/' }, { name: 'DecantX', url: 'https://www.decantx.com/' }, { name: 'Decants R Us', url: 'https://decantsrus.com/' }, { name: 'Scent Decant', url: 'https://www.scentdecant.com/' }],
  CA: [{ name: 'The Scented', url: 'https://thescented.ca/' }, { name: 'Cologne Decanted', url: 'https://colognedecanted.ca/' }, { name: 'Scentrique', url: 'https://www.scentrique.com/collections/fragrance-samples-discovery-sets' }],
  AE: [{ name: 'Decant.ae', url: 'https://decant.ae/' }, { name: 'TryScents', url: 'https://tryscentsae.com/' }],
  IN: [{ name: 'Perfume Gyaan', url: 'https://perfumegyaan.com/collections/decants' }, { name: 'Scentira', url: 'https://scentira.in/' }, { name: 'Scentoria', url: 'https://scentoria.co.in/collections/decants' }],
  SG: [{ name: 'The Decant Lab', url: 'https://thedecantlab.co/' }],
  MY: [{ name: 'Scentflix', url: 'https://www.scentflix.com/' }],
  PH: [{ name: '2Sprays', url: 'https://2sprays.com/' }, { name: 'Perfume Love Manila', url: 'https://perfumelovemanila.com/' }],
  AU: [{ name: 'Fragrance ADDX', url: 'https://www.fragranceaddx.com.au/' }],
  NZ: [{ name: 'Scent Droplet', url: 'https://scentdroplet.co.nz/' }, { name: 'NZ Fragrance', url: 'https://nzfragrance.com/' }, { name: 'Decant Dealer', url: 'https://decantdealer.co.nz/' }],
  ZA: [{ name: 'Niche Perfumery', url: 'https://nicheperfumery.co.za/product-category/decants/' }, { name: 'Exclusif Scents', url: 'https://exclusif.co.za/' }],
  AR: [{ name: 'Decants Argentina', url: 'https://decantsargentina.com/' }, { name: 'Perfumistas', url: 'https://www.perfumistas.com.ar/' }],
  BR: [{ name: 'Perfume Shopping', url: 'https://www.perfumeshopping.com.br/' }],
  MX: [{ name: 'MX Decants', url: 'https://mxdecants.com/' }, { name: 'Alambique', url: 'https://www.elalambiquemx.com/' }, { name: 'ABSCENTS', url: 'https://abscents.com.mx/' }],
  CL: [{ name: 'PerfumeView', url: 'https://shop.perfumeview.com/' }, { name: 'Decants Lab', url: 'https://decantslab.cl/' }, { name: 'Decantopia', url: 'https://decantopia.cl/' }],
};

const europeanProviders: readonly Provider[] = [
  { name: 'Essenza Nobile', url: 'https://essenza-nobile.de/sample-orders' },
  { name: 'Eurodecants', url: 'https://eurodecants.com/' },
  { name: 'Visionary Fragrances EU', url: 'https://visionaryfragranceseu.com/' },
];

const providersFor = (code: string) => providersByCountry[code] ?? (europeanUnion.has(code) ? europeanProviders : []);
const coveredCountries = [...Object.keys(providersByCountry), ...europeanUnion];
const hostOf = (url: string) => new URL(url).hostname.replace(/^www\./, '');

export function DecantProviders({
  country,
  countries,
  onCountryChange,
}: {
  country: string;
  countries: Country[];
  onCountryChange: (code: string) => void;
}) {
  const selected = countries.find((item) => item.code === country);
  const isEuropeanUnion = europeanUnion.has(country) && !providersByCountry[country];
  const providers = providersFor(country);

  return (
    <div className="decant-providers">
      <div className="decant-providers-heading">
        <div>
          <h3>Find decant providers</h3>
          <p>Choose your country to see names from our current list.</p>
        </div>
        <div className="decant-country">
          <span className="decant-country-label" aria-hidden="true">Your country</span>
          <CountryCombobox
            country={country}
            countries={countries}
            onChange={onCountryChange}
            label="Your country"
            featured={{ codes: coveredCountries, label: 'With providers on our list', restLabel: 'All countries' }}
            hint={(code) => {
              const count = providersFor(code).length;
              return count ? `${count} ${count === 1 ? 'provider' : 'providers'}` : undefined;
            }}
          />
        </div>
      </div>
      <div className="decant-providers-results" aria-live="polite">
        {providers.length ? (
          <>
            <p className="decant-providers-result-title">
              {isEuropeanUnion ? `European options for ${selected?.name}` : `In ${selected?.name}`}
            </p>
            <ul>
              {providers.map((provider) => (
                <li key={provider.name}>
                  {provider.url ? (
                    <a className="decant-provider" href={provider.url} target="_blank" rel="nofollow noopener noreferrer">
                      <span className="decant-provider-name">{provider.name}</span>
                      <span className="decant-provider-site">{hostOf(provider.url)}</span>
                      <span className="sr-only"> (opens in a new tab)</span>
                    </a>
                  ) : (
                    <span className="decant-provider is-unlinked">
                      <span className="decant-provider-name">{provider.name}</span>
                      <span className="decant-provider-site">Website not yet listed</span>
                    </span>
                  )}
                </li>
              ))}
            </ul>
            <p className="decant-providers-note">Links open the provider’s own website. Check the current range, delivery to your address, and returns before ordering.</p>
          </>
        ) : (
          <p className="decant-providers-empty">
            {country ? `We do not yet have a decant provider listed for ${selected?.name ?? 'this country'}. Try a fragrance counter or the brand’s own sample programme.` : 'Choose a country to see the providers on our list.'}
          </p>
        )}
      </div>
    </div>
  );
}
